import { useCallback, useMemo } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { ProductPageLayout } from '~/components/ProductPageLayout/ProductPageLayout'
import {
  PRODUCT_UI,
  isProductKey,
} from '~/components/ProductPage/productTypes/registry'
import { UrlConfigProvider } from '~/context/urlConfigContext'
import { useUrlRead } from '~/utils/useUrlSync'
import {
  normalizeConfig,
  parseQueryToConfig,
  type ProductKey as PKey,
} from '~/utils/configUrl'
import type { BaseConfig, Constraints } from '~/utils/configTypes'
import { getConstraints } from '~/config/furnitureConstraints'
import {
  useFurnitureBySku,
  furnitureToConstraintsPatch,
} from '~/utils/furnitureBySku'
import { FurnitureProvider } from '~/context/furnitureContext'
import { PromoLeadPopup } from '~/components/PromoLeadPopup/PromoLeadPopup'

function readQuery(): Record<string, string | string[] | undefined> {
  let search = typeof window !== 'undefined' ? window.location.search : ''
  const hash = typeof window !== 'undefined' ? window.location.hash : ''

  // Salvage deeplinks where an unencoded '#' in a value truncated the query.
  // e.g. `?...&color=#e7d5c3&columns=3&...` — the browser cuts at the first '#',
  // leaving search=`?...&color=` and hash=`#e7d5c3&columns=3&...` (real params).
  // Re-attach the fragment and re-encode the '#' so it parses back into `color`.
  // Guard on '=' so genuine anchors (#reviews) are left untouched. This app uses
  // BrowserRouter (path routing), so a param-like fragment is always a broken link.
  if (hash.length > 1 && hash.includes('=')) {
    search = `${search}${hash.replace('#', '%23')}`
  }

  const sp = new URLSearchParams(search)
  const out: Record<string, string | string[] | undefined> = {}
  sp.forEach((value, key) => {
    const existing = out[key]
    if (existing === undefined) out[key] = value
    else if (Array.isArray(existing)) existing.push(value)
    else out[key] = [existing, value]
  })
  return out
}

export default function DynamicConfiguratorPage() {
  const { product } = useParams<{ product: string }>()

  if (!product || !isProductKey(product)) {
    return <Navigate to="/catalog" replace />
  }

  // Load the backend furniture definition for this product and use it to drive
  // the dimension ranges + color palette. Falls back to static constraints when
  // the backend is unavailable or has no data for this SKU.
  const furniture = useFurnitureBySku(product)

  const baseConstraints = getConstraints(product) as Constraints
  const C = useMemo<Constraints>(() => {
    const patch = furniture ? furnitureToConstraintsPatch(furniture) : null
    if (!patch) return baseConstraints

    const d = baseConstraints.dimensions
    return {
      ...baseConstraints,
      dimensions: {
        ...d,
        width: patch.dimensions.width
          ? { ...d.width, ...patch.dimensions.width }
          : d.width,
        height: patch.dimensions.height
          ? { ...d.height, ...patch.dimensions.height }
          : d.height,
        depth: patch.dimensions.depth
          ? { ...d.depth, ...patch.dimensions.depth }
          : d.depth,
      },
      // Backend is authoritative once loaded: an empty chipboard list means no
      // colors, not "fall back to the static palette".
      colors: patch.colors,
    }
  }, [baseConstraints, furniture])
  const initial = useMemo<BaseConfig>(
    () =>
      normalizeConfig(
        parseQueryToConfig(readQuery(), product as PKey, C),
        product as PKey,
        C
      ),
    [product, C]
  )

  const { Shell } = PRODUCT_UI[product]
  const { config, setConfig: setBaseConfig } = useUrlRead<BaseConfig>(initial)

  const setConfigMerged = useCallback(
    (
      next:
        | Partial<BaseConfig>
        | ((prev: Partial<BaseConfig>) => Partial<BaseConfig>)
    ) => {
      if (typeof next === 'function') {
        setBaseConfig((prev) => ({ ...prev, ...next(prev) }))
      } else {
        setBaseConfig((prev) => ({ ...prev, ...next }))
      }
    },
    [setBaseConfig]
  )

  const ctxValue = useMemo(
    () => ({ config, setConfig: setConfigMerged, constraints: C }),
    [config, setConfigMerged, C]
  )

  return (
    <UrlConfigProvider value={ctxValue}>
      <FurnitureProvider value={furniture}>
        <ProductPageLayout fullWidth showBreadcrumbs={false} showDescription={false}>
          <Shell />
        </ProductPageLayout>
        <PromoLeadPopup product={product as PKey} />
      </FurnitureProvider>
    </UrlConfigProvider>
  )
}
